import { CircularProgress, Paper, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertData, AlertType, useAlert } from '../providers/alert'
import { FirstPartyPlatformType } from '../types/Account'

interface AuthCallbackProps<T> {
  usePostAuth: (authCode: string | null) => {
    data: T | null
    loading: boolean
    error: Error | null
    callFetch: () => void
  }
  onSuccess: (data: T) => void
  platform: FirstPartyPlatformType
}

function AuthCallback<T>({ usePostAuth, onSuccess, platform }: AuthCallbackProps<T>) {
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { t } = useTranslation('vpc', { keyPrefix: 'login' })

  const params = new URLSearchParams(location.search)
  const authCode = params.get('code')

  const { data, loading, error, callFetch } = usePostAuth(authCode)
  const handledErrorsRef = useRef<Set<string>>(new Set())

  // translation keys
  const platformNameKey = `${platform}.name`.toLowerCase()

  // trigger jwt fetch
  useEffect(() => {
    if (authCode !== null && data === null && error === null && !loading) {
      callFetch()
    }
  }, [authCode, data, error, callFetch, loading])

  // set auth when jwt is returned
  useEffect(() => {
    if (data !== null) {
      onSuccess(data)
    }
  }, [data, onSuccess])

  // handle fetch error
  useEffect(() => {
    if (!error) {
      return
    }

    if (handledErrorsRef.current.has(error.message)) {
      return
    }
    handledErrorsRef.current.add(error.message)

    const alert: AlertData = {
      msg: t('failed', { platformName: t(platformNameKey) }),
      severity: AlertType.ERROR,
      duration: 5000,
    }
    console.error(alert.msg, error.message)
    showAlert(alert)
    const timeoutId = setTimeout(() => {
      navigate('/')
    }, 4000)

    // clear the timeout if the component is unmounted
    return () => {
      clearTimeout(timeoutId)
    }
  }, [error])

  // handle missing auth code
  useEffect(() => {
    if (authCode) {
      return
    }
    const alert: AlertData = {
      msg: t('missingAuthCode', { platformName: t(platformNameKey) }),
      severity: AlertType.ERROR,
      duration: 5000,
    }

    showAlert(alert)

    const timeoutId = setTimeout(() => navigate('/'), 4000)

    // clear the timeout if the component is unmounted
    return () => {
      clearTimeout(timeoutId)
    }
  }, [authCode])

  return (
    <Paper sx={{ p: 4, textAlign: 'center', justifySelf: 'center' }}>
      <Typography variant="h4" gutterBottom data-testid="auth-callback-title">
        {t('success', { platformName: t(platformNameKey) })}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {t('redirecting')}
      </Typography>
      <CircularProgress />
    </Paper>
  )
}

export default AuthCallback
